<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Appointment</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Schedule a new appointment between doctors and patients
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-calendar-plus</v-icon>
                      New Appointment Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Appointments
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Appointment Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-account-multiple</v-icon>
                    Appointment Details
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        ref="doctor"
                        v-model="form.doctor_id"
                        :items="doctors"
                        label="Doctor"
                        placeholder="Select doctor"
                        outlined
                        :error-messages="errors.doctor_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                        :rules="[(v) => !!v || 'Doctor is required']"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-doctor</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        ref="patient"
                        v-model="form.patient_id"
                        :items="patients"
                        label="Patient"
                        placeholder="Select patient"
                        outlined
                        :error-messages="errors.patient_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                        :rules="[(v) => !!v || 'Patient is required']"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-heart</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <!-- Doctor Availability + Slot Picker -->
                  <v-row v-if="form.doctor_id">
                    <v-col cols="12">
                      <v-card class="availability-card mt-1 mb-2" elevation="1">

                        <!-- Header -->
                        <div class="availability-header px-4 py-3 d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="white" size="18" class="mr-2">mdi-calendar-clock</v-icon>
                            <span class="white--text font-weight-semibold text-body-2">Doctor Availability</span>
                            <v-progress-circular v-if="loadingAvailability" indeterminate size="14" width="2" color="white" class="ml-2"></v-progress-circular>
                          </div>
                          <!-- Selected slot badge -->
                          <div v-if="form.appointment_time" class="d-flex align-center">
                            <v-icon color="white" size="14" class="mr-1">mdi-check-circle</v-icon>
                            <span class="white--text text-caption font-weight-medium">{{ appointmentTimeFormatted }}</span>
                            <v-btn icon x-small color="white" class="ml-1" @click="clearSlot">
                              <v-icon size="14">mdi-close</v-icon>
                            </v-btn>
                          </div>
                        </div>

                        <v-card-text class="pa-4">
                          <!-- Empty state -->
                          <div v-if="!loadingAvailability && doctorAvailability.length === 0" class="text-center py-4">
                            <v-icon size="36" color="grey lighten-2">mdi-calendar-remove</v-icon>
                            <p class="text-caption grey--text mt-2 mb-0">No availability configured for this doctor.</p>
                          </div>

                          <div v-if="!loadingAvailability && doctorAvailability.length > 0">

                            <!-- Step 1: Pick a date -->
                            <div class="d-flex align-center mb-4">
                              <div class="step-badge mr-3">1</div>
                              <span class="text-caption font-weight-medium grey--text text--darken-2 mr-3">Pick a date</span>
                              <v-menu
                                v-model="slotDateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    :value="slotDate"
                                    placeholder="Select date"
                                    outlined
                                    dense
                                    readonly
                                    hide-details
                                    clearable
                                    class="professional-field slot-date-field"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click:clear="clearSlot"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="primary" small>mdi-calendar</v-icon>
                                    </template>
                                  </v-text-field>
                                </template>
                                <v-date-picker
                                  v-model="slotDate"
                                  :min="new Date().toISOString().substr(0, 10)"
                                  :allowed-dates="isAllowedDate"
                                  color="primary"
                                  @change="slotDateMenu = false; selectedSlotId = null; form.date = ''; form.from_time = ''; form.to_time = ''"
                                ></v-date-picker>
                              </v-menu>
                              <v-chip v-if="slotDate" x-small color="primary" outlined class="ml-3">
                                <v-icon left x-small>mdi-calendar-check</v-icon>
                                {{ new Date(slotDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) }}
                              </v-chip>
                            </div>

                            <!-- Step 2: Pick a slot -->
                            <div class="d-flex align-start">
                              <div class="step-badge mr-3 mt-1">2</div>
                              <div class="flex-grow-1">
                                <span class="text-caption font-weight-medium grey--text text--darken-2 d-block mb-2">
                                  {{ slotDate ? 'Click a time slot to select' : 'Available slots (select a date first)' }}
                                </span>

                                <!-- Date selected: show only matching day slots -->
                                <div v-if="slotDate">
                                  <div v-if="slotsForSelectedDate.length === 0" class="text-caption grey--text">
                                    No slots available on this day.
                                  </div>
                                  <div v-for="day in slotsForSelectedDate" :key="day.id">
                                    <div class="d-flex flex-wrap">
                                      <div
                                        v-for="slot in day.available_time_slots"
                                        :key="slot.id"
                                        :class="['slot-pill mr-2 mb-2', selectedSlotId === slot.id ? 'slot-pill--selected' : '']"
                                        @click="selectSlot(slot, day)"
                                      >
                                        <v-icon v-if="selectedSlotId === slot.id" size="11" class="mr-1">mdi-check</v-icon>
                                        {{ slot.from }} – {{ slot.to }}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <!-- No date: show all days greyed out for reference -->
                                <div v-else>
                                  <div v-for="day in doctorAvailability" :key="day.id" class="d-flex align-start mb-2">
                                    <span class="day-label mr-2">{{ day.day.substring(0, 3) }}</span>
                                    <div class="d-flex flex-wrap">
                                      <div
                                        v-for="slot in day.available_time_slots"
                                        :key="slot.id"
                                        class="slot-pill slot-pill--disabled mr-2 mb-2"
                                      >
                                        {{ slot.from }} – {{ slot.to }}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        ref="status"
                        v-model="form.status"
                        :items="statuses"
                        label="Appointment Status"
                        placeholder="Select status"
                        outlined
                        :error-messages="errors.status"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-check</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Notes Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-note-text</v-icon>
                    Additional Notes
                  </h3>
                  
                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.notes"
                        label="Notes (Optional)"
                        placeholder="Add any additional notes or instructions for the appointment"
                        outlined
                        rows="4"
                        :error-messages="errors.notes"
                        class="professional-field"
                        hide-details="auto"
                        auto-grow
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-text</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>


              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip 
                      small 
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{ hasValidationErrors() ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                      </v-icon>
                      {{ hasValidationErrors() ? 'Please fix validation errors' : 'Form validation passed' }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>
                
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="isLoading"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="isLoading || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Appointment
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "AppointmentCreatePage",
  data() {
    return {
      valid: false,
      form: {
        doctor_id: "",
        patient_id: "",
        date: "",
        from_time: "",
        to_time: "",
        doctor_available_timeslot_id: null,
        status: false,
        notes: "",
        is_active: true,
      },
      errors: {},
      doctors: [],
      patients: [],
      statuses: [
        { id: "PENDING", text: "Pending", value: false },
        { id: "CONFIRMED", text: "Confirmed", value: true },
      ],
      isLoading: false,
      doctorAvailability: [],
      loadingAvailability: false,
      slotDate: "",
      slotDateMenu: false,
      selectedSlotId: null,
    };
  },
  watch: {
    "form.doctor_id"(val) {
      this.doctorAvailability = [];
      this.slotDate      = "";
      this.selectedSlotId = null;
      this.form.date      = "";
      this.form.from_time = "";
      this.form.to_time   = "";
      this.form.doctor_available_timeslot_id = null;
      if (val) this.fetchDoctorAvailability(val);
    },
  },
  computed: {
    appointmentTimeFormatted() {
      if (this.form.date && this.form.from_time) {
        const d = new Date(this.form.date + "T00:00:00").toLocaleDateString("en-US", {
          year: "numeric", month: "short", day: "numeric", weekday: "short",
        });
        return `${d}, ${this.form.from_time} – ${this.form.to_time}`;
      }
      return "";
    },
    slotsForSelectedDate() {
      if (!this.slotDate) return [];
      const dayName = new Date(this.slotDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" });
      return this.doctorAvailability.filter(d => d.day.toLowerCase() === dayName.toLowerCase());
    },
    availableDayNames() {
      return this.doctorAvailability.map(d => d.day.toLowerCase());
    },
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async fetchDoctorAvailability(doctorId) {
      this.loadingAvailability = true;
      try {
        const res = await axios.get(`/users?user_id=${doctorId}`);
        const user = res.data.data?.[0];
        this.doctorAvailability = (user?.doctor_available_days || []).filter(d => d.is_active);
      } catch (e) {
        this.doctorAvailability = [];
      } finally {
        this.loadingAvailability = false;
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("appointments/users");
        const users = response.data.data;
        
        // Filter users by position - doctors and patients
        const doctorUsers = users.filter(user => 
          user.position && user.position.name && 
          user.position.name.toLowerCase().includes('doctor')
        );
        
        const patientUsers = users.filter(user => 
          user.position && user.position.name && 
          user.position.name.toLowerCase().includes('patient')
        );
        
        // Populate doctors dropdown with users having doctor positions
        this.doctors = doctorUsers.map(user => ({
          id: user.id,
          text: user.name,
          value: user.id,
        }));
        
        // Populate patients dropdown with users having patient positions
        this.patients = patientUsers.map(user => ({
          id: user.id,
          text: user.name,
          value: user.id,
        }));
        
        // If no users found with specific positions, show all users in both dropdowns as fallback
        if (this.doctors.length === 0 && this.patients.length === 0) {
          this.doctors = users.map(user => ({
            id: user.id,
            text: user.name,
            value: user.id,
          }));
          
          this.patients = users.map(user => ({
            id: user.id,
            text: user.name,
            value: user.id,
          }));
        }
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    isAllowedDate(dateStr) {
      const dayName = new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
      return this.availableDayNames.includes(dayName);
    },
    selectSlot(slot) {
      this.selectedSlotId = slot.id;
      this.form.date                       = this.slotDate;
      this.form.from_time                  = slot.from;
      this.form.to_time                    = slot.to;
      this.form.doctor_available_timeslot_id = slot.id;
    },
    clearSlot() {
      this.selectedSlotId = null;
      this.form.date                       = "";
      this.form.from_time                  = "";
      this.form.to_time                    = "";
      this.form.doctor_available_timeslot_id = null;
      this.slotDate                        = "";
    },
    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.isLoading = true;
        this.errors = {};
        
        await axios.post("/appointments", this.form);
        this.isLoading = false;
        this.$router.push("/appointments");
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error creating appointment:", error);
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }
  
  .form-title {
    font-size: 0.9rem;
  }
  
  .section-title {
    font-size: 0.85rem;
  }
  
  .form-content {
    padding: 0.75rem !important;
  }
  
  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }
  
  .form-content {
    padding: 0.5rem !important;
  }
  
  .section-title {
    font-size: 0.8rem;
  }
  
  .professional-field >>> .v-input__control {
    min-height: 36px;
  }
  
  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Availability Card */
.availability-card {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.availability-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
}

/* Step badge */
.step-badge {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--v-primary-base);
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Slot date field */
.slot-date-field {
  max-width: 180px;
}

/* Day label */
.day-label {
  font-size: 11px;
  font-weight: 600;
  color: #1976d2;
  background: #e3f2fd;
  border-radius: 4px;
  padding: 2px 8px;
  min-width: 36px;
  text-align: center;
  margin-top: 2px;
  flex-shrink: 0;
}

/* Time slot pills */
.slot-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  border: 1.5px solid #43a047;
  color: #43a047;
  background: #fff;
  transition: all 0.15s ease;
  user-select: none;
}

.slot-pill:hover {
  background: #e8f5e9;
  border-color: #2e7d32;
  color: #2e7d32;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(67,160,71,0.25);
}

.slot-pill--selected {
  background: #43a047 !important;
  color: #fff !important;
  border-color: #2e7d32 !important;
  box-shadow: 0 2px 8px rgba(67,160,71,0.4);
}

.slot-pill--disabled {
  cursor: default;
  border-color: #bdbdbd;
  color: #9e9e9e;
  background: #fafafa;
}

.slot-pill--disabled:hover {
  transform: none;
  box-shadow: none;
  background: #fafafa;
  border-color: #bdbdbd;
  color: #9e9e9e;
}
</style>